'use client';

import { cn } from '@core/utils';
import type { Placement, UseFloatingReturn } from '@floating-ui/react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react';
import { Portal } from '@radix-ui/react-portal';
import { Slot } from '@radix-ui/react-slot';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import type { HTMLTagProps } from '../../../types';

const PopoverContext = createContext<
  | (UseFloatingReturn['context'] & {
      handleMouseOverChange: Dispatch<SetStateAction<boolean>>;
      handleOpen: Dispatch<SetStateAction<boolean>>;
      preventCloseOnContentClick?: boolean;
    })
  | null
>(null);

interface PopoverRootProps {
  children: ReactNode;
  onOpenChange?: (value: boolean) => void;
  defaultOpen?: boolean;
  floatingPadding?: number;
  iframeCompatibility?: boolean;
  maxFloatingHeight?: number;
  placement?: Placement;
  openOnHover?: boolean;
  preventCloseOnContentClick?: boolean;
  open?: boolean;
}

export const PopoverRoot = ({
  children,
  defaultOpen = false,
  onOpenChange,
  iframeCompatibility = false,
  floatingPadding = 10,
  maxFloatingHeight,
  placement,
  preventCloseOnContentClick,
  openOnHover = false,
  open,
}: PopoverRootProps) => {
  const [isOpenedInternal, setIsOpenedInternal] = useState(defaultOpen);
  const isOpened = open ?? isOpenedInternal;
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number>();

  const { context } = useFloating({
    onOpenChange: (value: boolean) => {
      setIsOpenedInternal(value);
      onOpenChange?.(value);
    },
    open: isOpened,
    placement,
    middleware: [
      offset(floatingPadding),
      flip(),
      shift({ padding: floatingPadding }),
      size({
        apply({ availableHeight }) {
          flushSync(() => {
            setMaxHeight(availableHeight - floatingPadding);
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  context.floatingStyles.maxHeight =
    maxFloatingHeight && maxHeight
      ? Math.min(maxHeight, maxFloatingHeight)
      : maxHeight;

  useEffect(() => {
    const handleClickOutside = () => {
      if (isMouseOver && (openOnHover || preventCloseOnContentClick)) return;

      setIsOpenedInternal(false);
      onOpenChange?.(false);
    };

    document.addEventListener('mousedown', handleClickOutside);

    if (openOnHover) {
      setIsOpenedInternal(isMouseOver);
      onOpenChange?.(isMouseOver);
    }

    const iframes = iframeCompatibility
      ? document.querySelectorAll('iframe')
      : null;

    //! iframe 클릭을 감지 못하는 문제가 있음 iframe 태그 자체에 이벤트를 걸어줘야 함
    if (iframeCompatibility) {
      iframes?.forEach((iframe) => {
        const attachListener = () => {
          if (iframe.contentWindow) {
            iframe.contentWindow.document.addEventListener(
              'mousedown',
              handleClickOutside,
            );
          }
        };

        iframe.addEventListener('load', attachListener);
        attachListener();
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (iframeCompatibility) {
        iframes?.forEach((iframe) => {
          if (iframe.contentWindow) {
            iframe.contentWindow.document.removeEventListener(
              'mousedown',
              handleClickOutside,
            );
          }
        });
      }
    };
  }, [
    iframeCompatibility,
    isMouseOver,
    onOpenChange,
    openOnHover,
    preventCloseOnContentClick,
  ]);

  return (
    <PopoverContext.Provider
      value={{
        ...context,
        handleOpen: setIsOpenedInternal,
        handleMouseOverChange: setIsMouseOver,
        preventCloseOnContentClick,
      }}
    >
      {children}
    </PopoverContext.Provider>
  );
};

interface PopoverTriggerProps extends Omit<HTMLTagProps<'button'>, 'ref'> {
  asChild?: boolean;
  disabled?: boolean;
}

export const PopoverTrigger = ({
  onClick,
  asChild,
  children,
  className,
  disabled = false,
  ...restProps
}: PopoverTriggerProps) => {
  const popoverContext = useContext(PopoverContext);

  if (!popoverContext)
    throw new Error(
      'Popover.Trigger 컴포넌트는 Popover.Root 컴포넌트 내부에서 사용되어야 합니다.',
    );

  const Button = asChild ? Slot : 'button';

  return (
    <Button
      {...restProps}
      ref={popoverContext.refs.setReference}
      className={cn('w-full', className)}
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (disabled) return;
        popoverContext.onOpenChange(!popoverContext.open);
        onClick?.(e);
      }}
      onMouseEnter={() => {
        if (disabled) return;
        popoverContext.handleMouseOverChange(true);
      }}
      onMouseLeave={() => {
        if (disabled) return;
        popoverContext.handleMouseOverChange(false);
      }}
    >
      {children}
    </Button>
  );
};

interface PopoverContentProps extends Omit<HTMLTagProps<'div'>, 'ref'> {
  asChild?: boolean;
  children?: ReactNode;
}

export const PopoverContent = ({
  asChild,
  children,
  className,
  onMouseEnter,
  onMouseLeave,
  ...restProps
}: PopoverContentProps) => {
  const popoverContext = useContext(PopoverContext);

  if (!popoverContext)
    throw new Error(
      'Popover.Trigger 컴포넌트는 Popover.Root 컴포넌트 내부에서 사용되어야 합니다.',
    );

  const Content = asChild ? Slot : 'div';

  return popoverContext.open ? (
    <Portal asChild>
      <Content
        ref={popoverContext.refs.setFloating}
        className={cn(
          'overflow-auto rounded-small bg-Gray-white p-8 shadow-popover',
          className,
        )}
        style={popoverContext.floatingStyles}
        onClick={() => {
          if (popoverContext.preventCloseOnContentClick) return;
          popoverContext.handleOpen(false);
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          popoverContext.handleMouseOverChange(true);
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          popoverContext.handleMouseOverChange(false);
          onMouseLeave?.(e);
        }}
        {...restProps}
      >
        {children}
      </Content>
    </Portal>
  ) : null;
};

export const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
};
